<template>
  <div class="goods-list qm-page-bg">
    <van-nav-bar :title="shopName" left-arrow class="qm-nav-bar" @click-left="$router.back()" />

    <div class="search-container">
      <van-search v-model="searchKeyword" placeholder="搜索商品名称..." shape="round" @input="onSearchInput" @clear="onSearchClear"
        clearable />
    </div>

    <div class="goods-container">
      <div v-for="item in goodsList" :key="item.goodsId" class="goods-item qm-glass-card" @click="handleItemClick(item)">
        <div class="goods-image">
          <img :src="item.goodsImg" :alt="item.goodsName" />
          <div class="sold-out-mask" v-if="item.isSoldOut">
            <span>售罄</span>
          </div>
        </div>
        <div class="goods-content">
          <div class="goods-info">
            <h3 class="goods-name">{{ item.goodsName }}</h3>
            <div class="goods-tags">
              <span v-for="tag in item.skuTagDataList" :key="tag" class="tag">{{
                tag
              }}</span>
            </div>
            <div class="goods-desc" v-html="item.customTag"></div>
          </div>
          <div class="goods-action">
            <div class="add-btn" :class="{ disabled: item.isSoldOut }">
              <van-icon name="plus" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <van-popup v-model="showDetail" position="bottom" round closeable close-icon="close" :style="{ height: '85%' }">
      <div class="detail-popup" v-if="goodsDetail">
        <div class="popup-handle"></div>
        <div class="scrollable-content">
          <div class="goods-image">
            <img :src="goodsDetail.goodsImg" :alt="goodsDetail.goodsName" />
          </div>

          <div class="goods-info">
            <h1 class="goods-name">{{ goodsDetail.goodsName }}</h1>
            <div class="goods-tags">
              <span v-for="tag in goodsDetail.skuTagDataList" :key="tag" class="tag">{{ tag }}</span>
            </div>
            <div class="goods-desc" v-html="goodsDetail.customTag"></div>
            <div v-if="goodsDetail.unitList && goodsDetail.unitList.length" class="unit-list">
              <div v-for="unit in goodsDetail.unitList" :key="unit.unitId" class="unit-item"
                :class="{ active: unit.isSelect }" @click="selectUnit(unit)">
                {{ unit.unitName }}
              </div>
            </div>
          </div>

          <template v-if="goodsDetail && goodsDetail.sortedPracticeList">
            <div v-for="group in goodsDetail.sortedPracticeList" :key="group.practiceName" class="specs-section">
              <div class="section-title">{{ group.practiceName }}</div>
              <div class="options-list">
                <div v-for="option in group.practiceValueList" :key="option.practiceValueId" class="option-item" :class="{
                  active:
                    selectedOptions[group.practiceName] ===
                    option.practiceValue,
                }" @click="
                  selectOption(group.practiceName, option.practiceValue)
                  ">
                  {{ option.practiceValue }}
                </div>
              </div>
            </div>
          </template>

          <template v-if="goodsDetail && goodsDetail.attachGroups">
            <div v-for="group in goodsDetail.attachGroups.filter(
              (g) => g.name === '底料'
            )" :key="group.id" class="specs-section">
              <div class="section-title">{{ group.name }}</div>
              <div class="attach-list">
                <div v-for="attach in group.attaches.filter(
                  (a) => !a.salePrice || a.salePrice === 0
                )" :key="attach.attachGoodsId" class="attach-item" :class="{
                  active: isAttachSelected(attach.attachGoodsId),
                  disabled: attach.inventory <= 0,
                }" @click="toggleAttach(attach)">
                  <div class="attach-info">
                    <img v-if="
                      attach.pictureUrlList && attach.pictureUrlList.length
                    " :src="attach.pictureUrlList[0]" class="attach-image" />
                    <span class="attach-name">{{
                      attach.attachGoodsName
                    }}</span>
                  </div>
                  <div class="attach-price">
                    <span v-if="attach.inventory <= 0" class="sold-out-text">已售罄</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="bottom-bar">
          <van-button type="primary" block class="qm-btn-primary" @click="goToSettlement" :disabled="!isValidSelection">
            确认结算
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import request from "@/utils/request";
import { mapState } from "vuex";

export default {
  name: "GoodsList",
  data() {
    return {
      shopName: "",
      shopAddress: "", // 1. 添加 shopAddress
      goodsList: [],
      allGoodsList: [], // 存储所有商品数据
      searchKeyword: "", // 搜索关键词
      searchTimer: null, // 搜索防抖定时器
      showDetail: false,
      currentGoods: null,
      goodsDetail: null,
      selectedOptions: {},
      selectedAttaches: [], // 选中的加料列表
      skuList: ["杯型", "杯", "容量", "规格","份量"]
    };
  },
  computed: {
    ...mapState(["appConfig"]),

    isValidSelection() {
      if (!this.goodsDetail) return false;

      // 1. 检查是否已选择杯型
      const isUnitSelected =
        this.goodsDetail.unitList &&
        this.goodsDetail.unitList.some((unit) => unit.isSelect);

      // 2. 检查是否所有必选的口味选项都已选择
      const areOptionsSelected = (
        this.goodsDetail.sortedPracticeList || []
      ).every((group) => {
        if (group.isRequired) {
          // isRequired > 0 表示必选
          return !!this.selectedOptions[group.practiceName];
        }
        return true;
      });

      // 3. 两者都满足时，才算有效选择
      return isUnitSelected && areOptionsSelected;
    },
  },
  created() {
    this.shopName = this.$route.query.shopName || "";
    this.shopAddress = this.$route.query.shopAddress || ""; // 2. 从路由获取 shopAddress
    this.fetchGoodsList();
  },
  methods: {
    async fetchGoodsList() {
      const loading = this.$toast.loading({
        message: "加载中...",
        forbidClick: true,
        duration: 0,
      });
      try {
        const shopId = this.$route.query.shopId;
        if (!shopId || !this.appConfig) {
          this.$toast.fail("缺少店铺ID或应用配置");
          loading.clear();
          return;
        }

        // 1. 获取基础商品列表
        let listResponse = await request.post("/qimai/goods-list", {
          storeId: this.appConfig.storeId,
          appid: this.appConfig.appid,
          dataId: this.appConfig.dataId,
          shopId: shopId,
        });

        // if(listResponse){
        //   listResponse = listResponse.data
        // }

        if (
          !listResponse ||
          !this.isSuccess(listResponse.code) ||
          !listResponse.data ||
          !listResponse.data.data
        ) {
          this.$toast.fail(listResponse.msg || "获取商品列表失败");
          this.goodsList = [];
          loading.clear();
          return;
        }

        const baseGoodsList = listResponse.data.data;
        if (baseGoodsList.length === 0) {
          this.goodsList = [];
          loading.clear();
          return;
        }

        // 2. 并发获取所有商品的详细信息
        const detailPromises = baseGoodsList.map((item) =>
          request.post("/qimai/goods-detail", {
            goodsId: item.goodsId,
            storeId: this.appConfig.storeId,
            shopId: shopId,
            appid: this.appConfig.appid,
          })
        );

        const detailResults = await Promise.allSettled(detailPromises);
        console.log(detailResults);

        // 3. 整合详细信息到最终的商品列表，并过滤掉详情获取失败或data为null的项
        const processedGoodsList = detailResults
          .map((result, index) => {
            const baseItem = baseGoodsList[index];
            if (
              result.status === "fulfilled" &&
              result.value &&
              this.isSuccess(result.value.code) &&
              result.value.data
            ) {
              const detailData = result.value.data;

              // --- 根据新规则决定杯型列表的数据源 ---
              const goodsListCupNames = (baseItem.goodsSkuList || [])
                .map(
                  (sku) =>
                    sku.skuItemList.find((si) =>
                      this.skuList.includes(si.specName)
                    )?.specValue
                )
                .filter(Boolean);

              let displayUnitList;

              // 特殊情况: goods-list只返回大杯，则意味着中杯和大杯都可选，此时听从 good-details 的
              if (
                goodsListCupNames.length === 1 &&
                goodsListCupNames[0] === "大杯"
              ) {
                displayUnitList = (detailData.sortedSpecList || [])
                  .filter((specGroup) =>
                    this.skuList.includes(specGroup.specName)
                  )
                  .flatMap((specGroup) =>
                    specGroup.specValueList.map((spec) => ({
                      unitId: spec.specValueId,
                      unitName: spec.specValue,
                      isSelect: false,
                    }))
                  );
              } else {
                // 常规情况: good-list返回什么就显示什么
                displayUnitList = (baseItem.goodsSkuList || []).map((sku) => {
                  const specItem = sku.skuItemList.find((si) =>
                    this.skuList.includes(si.specName)
                  );
                  return {
                    unitId: specItem ? specItem.specValueId : sku.skuId,
                    unitName: specItem ? specItem.specValue : "标准",
                    isSelect: false,
                  };
                });
              }

              return {
                ...baseItem,
                ...detailData,
                goodsName: detailData.name,
                goodsImg:
                  detailData.pictureUrlList &&
                    detailData.pictureUrlList.length > 0
                    ? detailData.pictureUrlList[0]
                    : "",
                isSoldOut: detailData.totalInventory <= 0,
                skuTagDataList: (detailData.labelList || []).map((l) => l.text),
                customTag: detailData.detail,
                displayUnitList, // 存储权威的、可显示的杯型列表
              };
            }
            // 对于失败或data为null的情况，返回null，以便后续过滤
            console.error(
              `获取商品 ${baseItem.name} 详情失败或数据为空:`,
              result.reason || result.value
            );
            return null;
          })
          .filter((item) => item !== null); // 过滤掉所有为null的项

        // 根据优惠券限制过滤超过20元的商品
        let filteredGoodsList = processedGoodsList;
        const memberCoupon = this.$store.state.memberCoupon;
        if (
          memberCoupon &&
          memberCoupon.couponTemplate &&
          ["1246765280675201025"].includes(
            memberCoupon.couponTemplate.id
          )
        ) {
          filteredGoodsList = processedGoodsList.filter((item) => {
            const skuPrices = (item.goodsSkuList || []).map(
              (sku) => sku.salePrice || Infinity
            );
            const minPrice =
              skuPrices.length > 0 ? Math.min(...skuPrices) : Infinity;
            return minPrice <= 20;
          });
        }
        // 保存所有商品数据用于搜索
        this.allGoodsList = filteredGoodsList;
        this.goodsList = filteredGoodsList;
      } catch (error) {
        console.error("获取商品列表失败:", error);
        this.$toast.fail("获取商品列表失败");
      } finally {
        loading.clear();
      }
    },
    handleItemClick(item) {
      if (item.isSoldOut) {
        this.$toast("该商品已售罄");
        return;
      }
      if (item._loadError) {
        this.$toast.fail("商品信息加载失败，无法查看详情");
        return;
      }
      this.showGoodsDetail(item);
    },
    // 添加一个判断接口成功的方法
    isSuccess(code) {
      return code === 0 || code === "0" || code === "00" || code === "000";
    },
    showGoodsDetail(item) {
      console.log(item);

      // API请求已前置，此处不再请求，直接使用传入的完整item对象
      this.currentGoods = item;
      this.selectedOptions = {}; // 重置选项
      this.selectedAttaches = []; // 重置加料选项

      try {
        // 1. 从 item 中获取权威的、预处理好的 displayUnitList
        let finalUnitList = item.displayUnitList || [];

        // 2. 默认选中第一项，并确保响应性
        if (finalUnitList.length > 0) {
          finalUnitList = finalUnitList.map((unit, index) => ({
            ...unit,
            isSelect: index === 0,
          }));
        }
        console.log("============",item.attachGoodsList);

        // 屏蔽掉底料要钱的选项
        item.sortedPracticeList = item.sortedPracticeList
          .map((practice) => ({
            ...practice,
            practiceValueList: practice.practiceValueList.filter(
              (value) => !value.price || value.price == 0
            ),
          }))
          .filter((practice) => practice.practiceValueList.length > 0);

        // 屏蔽掉 attachGoodsList 中要钱的底料
        if (item.attachGoodsList && Array.isArray(item.attachGoodsList)) {
          item.attachGoodsList = item.attachGoodsList.filter(
            (attach) => !attach.attachGoodsPrice || attach.attachGoodsPrice == 0
          );
        }



        console.log("[debug] attachGoodsList:", item.attachGoodsList);
        console.log("[debug] attachGroups:", item.attachGroups);


        // 把 attachGoodsList 的免费项合并到 attachGroups 的"底料"分组
        if (item.attachGoodsList && item.attachGoodsList.length > 0) {
          if (!Array.isArray(item.attachGroups)) {
            item.attachGroups = [];
          }
          let diLiaoGroup = item.attachGroups.find((g) => g.name === "底料");
          if (!diLiaoGroup) {
            diLiaoGroup = {
              id: "diliao_default",
              name: "底料",
              attaches: [],
            };
            item.attachGroups.push(diLiaoGroup);
          }
          if (!Array.isArray(diLiaoGroup.attaches)) {
            diLiaoGroup.attaches = [];
          }
          item.attachGoodsList.forEach((attach) => {
            const exists = diLiaoGroup.attaches.some(
              (a) => a.attachGoodsId === attach.attachGoodsId
            );
            if (!exists) {
              diLiaoGroup.attaches.push({
                ...attach,
                salePrice: attach.attachGoodsPrice || 0,
              });
            }
          });
          console.log("[debug] 合并后 attachGroups:", item.attachGroups);
        }

        // 3. 转换做法/选项 (如温度、甜度)
        const modifyList = (item.sortedPracticeList || []).map((group) => ({
          typeName: group.practiceName,
          groupMinChoose: group.isRequired,
          data: group.practiceValueList.map((opt) => ({
            goodsId: opt.practiceValueId,
            goodsName: opt.practiceValue,
            isSelect: opt.isDefault === 1,
          })),
        }));

        // 4. 组合成UI需要的数据结构
        this.goodsDetail = {
          ...item, // item已经是完整的详情数据了
          unitList: finalUnitList, // 使用我们处理好的权威列表
          modifyList,
        };

        // 5. 设置默认选中的选项
        if (this.goodsDetail.modifyList) {
          this.goodsDetail.modifyList.forEach((group) => {
            const defaultOption =
              group.data.find((opt) => opt.isSelect) || group.data[0];
            if (defaultOption) {
              this.$set(
                this.selectedOptions,
                group.typeName,
                defaultOption.goodsName
              );
            }
          });
        }

        // 6. 默认选中第一个免费底料（如果有的话）
        if (this.goodsDetail.attachGroups) {
          const diLiaoGroup = this.goodsDetail.attachGroups.find(
            (g) => g.name === "底料"
          );
          if (diLiaoGroup) {
            const freeAttaches = diLiaoGroup.attaches.filter(
              (a) => (!a.salePrice || a.salePrice === 0) && a.inventory > 0
            );
            if (freeAttaches.length > 0) {
              // 默认选中第一个
              this.selectedAttaches.push({
                attachGoodsId: freeAttaches[0].attachGoodsId,
                attachGoodsName: freeAttaches[0].attachGoodsName,
                attachGoodsPrice: freeAttaches[0].salePrice || 0,
                attachSkuId: freeAttaches[0].attachGoodsId,
                num: 1,
              });
            }
          }
        }

        this.showDetail = true;
      } catch (error) {
        console.error("处理商品详情出错：", error);
        this.$toast.fail("打开商品详情失败");
      }
    },
    selectOption(groupName, optionName) {
      this.$set(this.selectedOptions, groupName, optionName);
    },
    // 判断加料是否被选中
    isAttachSelected(attachGoodsId) {
      return this.selectedAttaches.some(
        (a) => a.attachGoodsId === attachGoodsId
      );
    },
    // 切换加料选择状态
    toggleAttach(attach) {
      if (attach.inventory <= 0) {
        this.$toast("该加料已售罄");
        return;
      }

      const index = this.selectedAttaches.findIndex(
        (a) => a.attachGoodsId === attach.attachGoodsId
      );

      if (index > -1) {
        // 已选中，准备取消 — 若该项属于"底料"分组且分组内仅有 1 个免费项，则不允许取消
        const diLiaoGroup = (this.goodsDetail.attachGroups || []).find(
          (g) => g.name === "底料"
        );
        if (diLiaoGroup) {
          const freeAttaches = (diLiaoGroup.attaches || []).filter(
            (a) => !a.salePrice || a.salePrice === 0
          );
          const isInDiLiao = freeAttaches.some(
            (a) => a.attachGoodsId === attach.attachGoodsId
          );
          if (isInDiLiao && freeAttaches.length === 1) {
            this.$toast("底料为必选项,不能取消");
            return;
          }
        }
        // 取消选择
        this.selectedAttaches.splice(index, 1);
      } else {
        // 未选中，添加选择
        this.selectedAttaches.push({
          attachGoodsId: attach.attachGoodsId,
          attachGoodsName: attach.attachGoodsName,
          attachGoodsPrice: attach.salePrice,
          attachSkuId: attach.attachGoodsId, // 根据实际需要调整
          num: 1,
        });
      }
    },
    goToSettlement() {
      if (!this.isValidSelection) {
        this.$toast("请选择必选项");
        return;
      }

      const selectedUnit = this.goodsDetail.unitList.find((u) => u.isSelect);
      let selectedSku = this.goodsDetail.goodsSkuList.find((sku) =>
        sku.skuItemList.some((item) => item.specValue === selectedUnit.unitName)
      );

      console.log("goodsDetail", this.goodsDetail);

      if (!selectedSku) {
        selectedSku = this.goodsDetail.unitList[0];
        //   this.$toast.fail("未找到匹配的商品规格");
        //   return;
      }

      const practiceList = [];
      (this.goodsDetail.sortedPracticeList || []).forEach((group) => {
        const selectedOptionName = this.selectedOptions[group.practiceName];
        if (selectedOptionName) {
          const selectedOption = group.practiceValueList.find(
            (opt) => opt.practiceValue === selectedOptionName
          );
          if (selectedOption) {
            practiceList.push({
              id: group.practiceId,
              valueId: selectedOption.practiceValueId,
              value: selectedOption.practiceValue,
              price: selectedOption.price || 0,
            });
          }
        }
      });

      // 计算总价（基础价格 + 加料价格）
      const attachTotalPrice = this.selectedAttaches.reduce(
        (sum, attach) => sum + attach.attachGoodsPrice,
        0
      );

      const orderItem = {
        goodsId: this.goodsDetail.goodsId,
        type: this.goodsDetail.type || 1,
        name: this.goodsDetail.goodsName,
        num: 1,
        price: selectedSku.salePrice + attachTotalPrice,
        image: this.goodsDetail.goodsImg,
        skuId: selectedSku.skuId ? selectedSku.skuId : selectedSku.unitId,
        practiceList,
        skuInfo: {
          ...this.selectedOptions,
          杯型: selectedUnit.unitName,
        },
        combinedList: [],
        attachList: this.selectedAttaches.map((attach) => ({
          id: attach.attachGoodsId,
          name: attach.attachGoodsName,
          num: 1,
        })),
        isGoodsCoupon: 0,
        userId: "",
        openId: "",
        multiCombinedList: [],
        uniqueId: `${Date.now()}-0`,
        energyText: "",
      };

      const orderInfo = {
        orderType: "1",
        orderSubType: "EAT_IN",
        businessScene: "wxa",
        channel: "2",
        shopId: this.$route.query.shopId,
        shopName: this.shopName,
        shopAddress: this.shopAddress, // 3. 将 shopAddress 添加到 orderInfo
        itemList: [orderItem],
      };

      this.$router.push({
        path: "/goods-detail",
        query: {
          orderInfo: JSON.stringify(orderInfo),
          remark: "", // 备注字段暂时为空
        },
      });
    },
    // 生成UUID
    generateUUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    // 格式化日期为 yyyyMMddHHmmss
    formatDate(date) {
      const pad = (num) => (num < 10 ? "0" + num : num);
      return (
        `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
          date.getDate()
        )}` +
        `${pad(date.getHours())}${pad(date.getMinutes())}${pad(
          date.getSeconds()
        )}`
      );
    },
    // 新增：选择规格容量
    selectUnit(unit) {
      // 必须通过创建新数组的方式来触发响应式更新
      this.goodsDetail.unitList = this.goodsDetail.unitList.map((u) => ({
        ...u,
        isSelect: u.unitId === unit.unitId,
      }));
    },
    // 搜索输入处理（防抖）
    onSearchInput() {
      // 清除之前的定时器
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }

      // 设置新的定时器，300ms后执行搜索
      this.searchTimer = setTimeout(() => {
        this.performSearch();
      }, 300);
    },
    // 搜索清除处理
    onSearchClear() {
      this.searchKeyword = "";
      this.goodsList = this.allGoodsList;
    },
    // 执行搜索
    performSearch() {
      if (!this.searchKeyword.trim()) {
        // 如果搜索关键词为空，显示所有商品
        this.goodsList = this.allGoodsList;
        return;
      }

      const keyword = this.searchKeyword.toLowerCase().trim();

      // 根据商品名称、标签和描述进行搜索
      this.goodsList = this.allGoodsList.filter((item) => {
        // 搜索商品名称
        const nameMatch =
          item.goodsName && item.goodsName.toLowerCase().includes(keyword);

        // 搜索商品标签
        const tagMatch =
          item.skuTagDataList &&
          item.skuTagDataList.some((tag) =>
            tag.toLowerCase().includes(keyword)
          );

        // 搜索商品描述（去除HTML标签后搜索）
        const descMatch =
          item.customTag &&
          item.customTag
            .replace(/<[^>]*>/g, "")
            .toLowerCase()
            .includes(keyword);

        return nameMatch || tagMatch || descMatch;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.goods-list {
  min-height: 100vh;
}

.search-container {
  padding: 8px 16px 12px;
  border-bottom: 1px solid var(--qm-border);

  :deep(.van-search) {
    padding: 0;
    background: transparent;
  }
}

.goods-container {
  padding: 12px 16px 24px;
}

.goods-item {
  display: flex;
  padding: 14px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: var(--qm-transition);
  gap: 14px;

  &:active {
    background: var(--qm-bg-card-hover);
    border-color: var(--qm-border-active);
  }
}

.goods-image {
  width: 100px;
  height: 100px;
  border-radius: var(--qm-radius-md);
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  border: 1px solid var(--qm-border);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .sold-out-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      color: var(--qm-text-muted);
      font-size: 14px;
      font-weight: 600;
      padding: 4px 12px;
      border: 1px solid var(--qm-border);
      border-radius: var(--qm-radius-sm);
    }
  }
}

.goods-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.goods-info {
  .goods-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--qm-text-primary);
    margin: 0 0 8px;
    line-height: 1.4;
  }

  .goods-tags {
    margin-bottom: 6px;

    .tag {
      display: inline-block;
      padding: 2px 8px;
      margin-right: 6px;
      background: rgba(124, 111, 247, 0.12);
      color: var(--qm-secondary-light);
      border: 1px solid rgba(124, 111, 247, 0.25);
      border-radius: 6px;
      font-size: 11px;
    }
  }

  .goods-desc {
    font-size: 12px;
    color: var(--qm-text-muted);
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
}

.goods-action {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  margin-top: 8px;

  .add-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--qm-accent), var(--qm-accent-dark));
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 12px var(--qm-accent-glow);

    .van-icon {
      color: #1a1200;
      font-size: 16px;
      font-weight: bold;
    }

    &.disabled {
      opacity: 0.35;
      box-shadow: none;
    }
  }
}

.detail-popup {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--qm-bg-surface);

  .popup-handle {
    width: 36px;
    height: 4px;
    background: var(--qm-border);
    border-radius: 2px;
    margin: 10px auto 0;
  }
}

.scrollable-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  .goods-image {
    width: 100%;
    height: 260px;
    border: none;
    border-radius: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .goods-info {
    padding: 20px 16px;

    .goods-name {
      font-size: 22px;
      font-weight: 700;
      color: var(--qm-text-primary);
      margin: 0 0 12px;
    }

    .goods-tags {
      margin-bottom: 12px;

      .tag {
        display: inline-block;
        padding: 4px 10px;
        margin-right: 8px;
        background: rgba(124, 111, 247, 0.12);
        color: var(--qm-secondary-light);
        border: 1px solid rgba(124, 111, 247, 0.25);
        border-radius: var(--qm-radius-xl);
        font-size: 12px;
      }
    }

    .goods-desc {
      font-size: 14px;
      color: var(--qm-text-secondary);
      line-height: 1.6;
    }
  }
}

.specs-section {
  padding: 0 16px 16px;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--qm-accent);
    margin-bottom: 10px;
    letter-spacing: 0.5px;
  }
}

.options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .option-item {
    padding: 8px 16px;
    background: var(--qm-bg-chip);
    border-radius: var(--qm-radius-xl);
    font-size: 13px;
    cursor: pointer;
    border: 1px solid var(--qm-border);
    color: var(--qm-text-secondary);
    transition: var(--qm-transition);

    &.active {
      background: rgba(245, 166, 35, 0.12);
      border-color: var(--qm-accent);
      color: var(--qm-accent);
      font-weight: 600;
    }
  }
}

.bottom-bar {
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(20px);
  padding: 12px 16px;
  border-top: 1px solid var(--qm-border);

  .van-button {
    height: 50px;
    font-size: 16px;
  }
}

.unit-list {
  display: flex;
  gap: 10px;
  margin: 14px 0;
}

.unit-item {
  padding: 8px 20px;
  border: 1px solid var(--qm-border);
  border-radius: var(--qm-radius-xl);
  font-size: 14px;
  cursor: pointer;
  background: var(--qm-bg-chip);
  color: var(--qm-text-secondary);
  transition: var(--qm-transition);

  &.active {
    border-color: var(--qm-accent);
    color: var(--qm-accent);
    background: rgba(245, 166, 35, 0.12);
    font-weight: 600;
  }
}

.attach-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attach-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  background: var(--qm-bg-chip);
  border-radius: var(--qm-radius-sm);
  cursor: pointer;
  border: 1px solid var(--qm-border);
  transition: var(--qm-transition);

  &.active {
    background: rgba(245, 166, 35, 0.1);
    border-color: var(--qm-accent);
  }

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .attach-info {
    display: flex;
    align-items: center;
    gap: 10px;

    .attach-image {
      width: 40px;
      height: 40px;
      border-radius: var(--qm-radius-sm);
      object-fit: cover;
    }

    .attach-name {
      font-size: 14px;
      color: var(--qm-text-primary);
    }
  }

  .attach-price {
    .sold-out-text {
      color: var(--qm-text-muted);
      font-size: 12px;
    }
  }
}
</style>